import React, { useState, useRef, useEffect } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Alert, Image, Platform, Animated } from 'react-native';
import { useRouter, useLocalSearchParams, useFocusEffect } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { LinearGradient } from 'expo-linear-gradient';
import * as ImagePicker from 'expo-image-picker';
import { Feather } from '@expo/vector-icons';
import LiveCameraView, { LiveCameraViewRef } from '../../src/components/inventory/LiveCameraView';
import { Paprika, PaprikaTypography, PaprikaSmartInventory, PaprikaRadius } from '../../src/styles/theme';
import { shouldUseTestImage, getTestImageUri, simulateCameraDelay } from '../../src/utils/developmentUtils';
import { PantryLocation, PANTRY_LOCATIONS } from '../../src/utils/pantryLocation';
import { RequireAccountToScan } from '../../src/components/auth/ScanSignUp';

const InventoryCameraScreen: React.FC = () => {
  const router = useRouter();
  const { t } = useTranslation();
  const params = useLocalSearchParams<{ analysisMode?: string; zone?: string }>();
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentLoadingMessage, setCurrentLoadingMessage] = useState('');
  const [analysisMode, setAnalysisMode] = useState<'fridge' | 'receipt'>(
    params.analysisMode === 'receipt' ? 'receipt' : 'fridge'
  );
  // Incremented on every focus so <LiveCameraView> remounts and re-acquires a
  // fresh camera stream (see useFocusEffect below).
  const [cameraKey, setCameraKey] = useState(0);
  const zone = (PANTRY_LOCATIONS.includes(params.zone as any) ? (params.zone as PantryLocation) : undefined);
  const messageIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const progressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const progressAnim = useRef(new Animated.Value(0)).current;
  const liveCamRef = useRef<LiveCameraViewRef>(null);

  const getCarouselMessages = () => [
    t('inventory.scanCarousel1'),
    t('inventory.scanCarousel2'),
    t('inventory.scanCarousel3'),
    t('inventory.scanCarousel4'),
    t('inventory.scanCarousel5'),
    t('inventory.scanCarousel6'),
    t('inventory.scanCarousel7'),
  ];

  const startLoadingMessages = () => {
    const messages = getCarouselMessages();
    setCurrentLoadingMessage(messages[0]);
    let idx = 0;
    messageIntervalRef.current = setInterval(() => {
      idx = (idx + 1) % messages.length;
      setCurrentLoadingMessage(messages[idx]);
    }, 4000);

    // No fabricated progress here. This screen hands off to
    // recognition-results within ~16ms and never learns how the scan is going,
    // so any percentage it printed was invented — it used to climb to
    // "100% COMPLETE" in 12.5s and sit there for the rest of the scan. The bar
    // below is a fixed indeterminate sliver instead.
    progressAnim.setValue(0.15);
  };

  const stopLoadingMessages = () => {
    if (messageIntervalRef.current) {
      clearInterval(messageIntervalRef.current);
      messageIntervalRef.current = null;
    }
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
    setCurrentLoadingMessage('');
  };

  useEffect(() => {
    return () => {
      stopLoadingMessages();
    };
  }, []);

  // This screen is a hidden tab screen (app/(app)/_layout.tsx), so React
  // Navigation keeps it MOUNTED after the first visit — local state is not
  // reset on re-entry. Without this, a second capture re-renders the stale
  // `capturedImage` + `isProcessing` from the previous session (the "Curating
  // Your Inventory…" overlay) instead of the live camera (issue #488).
  // Reset transient state on every focus, and remount the camera so the web
  // getUserMedia stream is released and freshly re-acquired.
  const isFirstFocusRef = useRef(true);
  useFocusEffect(
    React.useCallback(() => {
      stopLoadingMessages();
      setCapturedImage(null);
      setIsProcessing(false);
      setCurrentLoadingMessage('');
      // The initial mount already has a fresh camera; only remount on re-entry
      // to avoid a redundant getUserMedia acquisition on first load.
      if (isFirstFocusRef.current) {
        isFirstFocusRef.current = false;
      } else {
        setCameraKey((k) => k + 1);
      }
    }, [])
  );

  const takePicture = async () => {
    try {
      if (shouldUseTestImage()) {
        await simulateCameraDelay();
        const testImageUri = await getTestImageUri(analysisMode);
        setCapturedImage(testImageUri);
        return;
      }

      const uri = await liveCamRef.current?.capturePhoto();
      if (uri) {
        setCapturedImage(uri);
      }
    } catch (error) {
      console.error('InventoryCameraScreen - Error taking picture:', error);
      Alert.alert('Error', t('inventory.takePictureFailed'));
    }
  };

  const retakePicture = () => {
    setCapturedImage(null);
    setIsProcessing(false);
  };

  const pickFromLibrary = async () => {
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ['images'],
      quality: 0.8,
      allowsEditing: false,
    });
    if (!result.canceled && result.assets[0]) {
      setCapturedImage(result.assets[0].uri);
    }
  };

  const usePicture = async () => {
    if (capturedImage && !isProcessing) {
      console.log('InventoryCameraScreen - Navigating to results screen for analysis');

      setIsProcessing(true);

      // Navigate immediately — analysis happens on the results screen
      // This avoids the 5-10s freeze from serializing AI results into route params
      setTimeout(() => {
        const navParams: Record<string, string> = {
          imageUri: capturedImage,
          analysisMode: analysisMode,
          isProcessing: 'true',
        };
        if (zone) {
          navParams.zone = zone;
        }
        router.replace({
          pathname: '/(app)/recognition-results',
          params: navParams
        });
      }, 16);
    }
  };

  // --- Post-capture preview state ---
  if (capturedImage) {
    return (
      <View style={styles.container}>
        {/* Glassmorphic Header */}
        <View style={styles.header}>
          <TouchableOpacity
            onPress={() => router.navigate('/(app)/inventory')}
            style={styles.headerButton}
          >
            <Text style={styles.headerButtonIcon}>✕</Text>
          </TouchableOpacity>
          <Text style={styles.headerTitle}>{t('smartInventory.title')}</Text>
          <TouchableOpacity style={styles.headerButton}>
            <Text style={styles.headerButtonIcon}>⋮</Text>
          </TouchableOpacity>
        </View>

        {isProcessing ? (
          <>
            {/* Editorial loading overlay */}
            <View style={styles.previewContainer}>
              <Image source={{ uri: capturedImage }} style={styles.capturedImage} />
              <LinearGradient
                colors={['rgba(251,249,246,0.4)', 'rgba(245,243,240,0.9)']}
                style={StyleSheet.absoluteFill}
              />
            </View>
            <View style={styles.analyzingContent}>
              <View style={styles.heroSection}>
                <Text style={styles.heroTitle}>{t('smartInventory.curating')}</Text>
                <View style={styles.heroLine} />
              </View>
              <View style={styles.valuePropSection}>
                <Text style={styles.valuePropText}>{currentLoadingMessage}</Text>
              </View>
              <View style={styles.progressSection}>
                <View style={styles.progressTrack}>
                  <Animated.View
                    style={[
                      styles.progressFill,
                      {
                        width: progressAnim.interpolate({
                          inputRange: [0, 1],
                          outputRange: ['0%', '100%'],
                        }),
                      },
                    ]}
                  />
                </View>
              </View>
            </View>
          </>
        ) : (
          <>
            {/* Image preview */}
            <View style={styles.previewContainer}>
              <Image source={{ uri: capturedImage }} style={styles.capturedImage} />
            </View>

            {/* Dark footer with actions */}
            <View style={styles.darkFooter}>
              <View style={styles.previewActions}>
                <TouchableOpacity
                  onPress={retakePicture}
                  style={styles.ghostButton}
                >
                  <Text style={styles.ghostButtonText}>
                    {t('inventory.retake')}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  onPress={usePicture}
                  style={styles.primaryButton}
                >
                  <Text style={styles.primaryButtonText}>
                    {analysisMode === 'fridge' ? t('inventory.analyzeFood') : t('inventory.scanReceipt')}
                  </Text>
                </TouchableOpacity>
              </View>
            </View>
          </>
        )}
      </View>
    );
  }

  // --- Pre-capture camera state ---
  return (
    <View style={styles.container}>
      {/* Glassmorphic Header */}
      <View style={styles.header}>
        <TouchableOpacity
          onPress={() => router.navigate('/(app)/inventory')}
          style={styles.headerButton}
        >
          <Text style={styles.headerButtonIcon}>✕</Text>
        </TouchableOpacity>
        <Text style={styles.headerTitle}>{t('smartInventory.title')}</Text>
        <TouchableOpacity style={styles.headerButton}>
          <Text style={styles.headerButtonIcon}>⋮</Text>
        </TouchableOpacity>
      </View>

      {/* Camera viewport */}
      <View style={styles.cameraViewport}>
        <LiveCameraView ref={liveCamRef} key={cameraKey} style={StyleSheet.absoluteFill} />

        {/* Dev mode indicator */}
        {shouldUseTestImage() && (
          <View style={styles.devModeIndicator}>
            <Text style={styles.devModeText}>{t('smartInventory.devMode')}</Text>
            <Text style={styles.devModeSubText}>{t('inventory.usingTestImage')}</Text>
          </View>
        )}

        {/* Scanner corner brackets */}
        <View style={styles.scannerFrame}>
          <View style={styles.scannerRow}>
            <View style={[styles.scannerCorner, styles.cornerTL]} />
            <View style={[styles.scannerCorner, styles.cornerTR]} />
          </View>

          {/* Center scanning pill */}
          <View style={styles.scanPillContainer}>
            <View style={styles.scanPill}>
              <Text style={styles.scanPillText}>
                {analysisMode === 'fridge' ? t('smartInventory.scanningPantry') : t('smartInventory.scanningReceipt')}
              </Text>
            </View>
          </View>

          <View style={styles.scannerRow}>
            <View style={[styles.scannerCorner, styles.cornerBL]} />
            <View style={[styles.scannerCorner, styles.cornerBR]} />
          </View>
        </View>

        {/* Gradient vignette */}
        <LinearGradient
          colors={['rgba(0,0,0,0.4)', 'transparent', 'rgba(0,0,0,0.6)']}
          style={StyleSheet.absoluteFill}
          pointerEvents="none"
        />
      </View>

      {/* Dark footer with controls */}
      <View style={styles.darkFooter}>
        {/* Mode toggle + settings */}
        <View style={styles.settingsBar}>
          <TouchableOpacity
            style={styles.settingButton}
            onPress={() => setAnalysisMode('fridge')}
          >
            <Text style={[
              styles.settingLabel,
              analysisMode === 'fridge' && styles.settingLabelActive
            ]}>
              {t('inventory.fridgePhoto')}
            </Text>
          </TouchableOpacity>
          <View style={styles.settingDivider} />
          <TouchableOpacity
            style={styles.settingButton}
            onPress={() => setAnalysisMode('receipt')}
          >
            <Text style={[
              styles.settingLabel,
              analysisMode === 'receipt' && styles.settingLabelActive
            ]}>
              {t('inventory.receiptScan')}
            </Text>
          </TouchableOpacity>
        </View>

        {/* Shutter row: [gallery] [shutter] [spacer] */}
        <View style={styles.shutterContainer}>
          <TouchableOpacity onPress={pickFromLibrary} style={styles.galleryButton} activeOpacity={0.7}>
            <Feather name="image" size={26} color="rgba(255,255,255,0.8)" />
          </TouchableOpacity>
          <TouchableOpacity onPress={takePicture} style={styles.shutterOuter} activeOpacity={0.8}>
            <View style={styles.shutterInner} />
          </TouchableOpacity>
          <View style={styles.galleryButton} />
        </View>

        {/* Hint text */}
        <Text style={styles.hintText}>
          {analysisMode === 'fridge'
            ? t('smartInventory.hintFridge')
            : t('smartInventory.hintReceipt')}
        </Text>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#0a0a0a',
  },
  // --- Header ---
  header: {
    ...PaprikaSmartInventory.glassmorphicHeader,
    paddingTop: Platform.OS === 'web' ? 16 : 50,
    zIndex: 50,
  },
  headerTitle: {
    ...PaprikaSmartInventory.headerTitle,
  },
  headerButton: {
    ...PaprikaSmartInventory.headerButton,
  },
  headerButtonIcon: {
    fontSize: 20,
    color: Paprika.accent,
    fontWeight: '500',
  },
  // --- Camera viewport ---
  cameraViewport: {
    flex: 1,
    backgroundColor: '#1a1a1a',
    justifyContent: 'center',
    alignItems: 'center',
  },
  scannerFrame: {
    width: '80%',
    aspectRatio: 3 / 4,
    justifyContent: 'space-between',
    zIndex: 10,
  },
  scannerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  scannerCorner: {
    ...PaprikaSmartInventory.scannerCorner,
  },
  cornerTL: {
    borderTopWidth: 2,
    borderLeftWidth: 2,
    borderTopLeftRadius: 12,
  },
  cornerTR: {
    borderTopWidth: 2,
    borderRightWidth: 2,
    borderTopRightRadius: 12,
  },
  cornerBL: {
    borderBottomWidth: 2,
    borderLeftWidth: 2,
    borderBottomLeftRadius: 12,
  },
  cornerBR: {
    borderBottomWidth: 2,
    borderRightWidth: 2,
    borderBottomRightRadius: 12,
  },
  scanPillContainer: {
    alignItems: 'center',
  },
  scanPill: {
    ...PaprikaSmartInventory.statusPill,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.2)',
  },
  scanPillText: {
    color: '#ffffff',
    fontSize: 10,
    fontFamily: 'Inter-SemiBold',
    fontWeight: '700',
    letterSpacing: 2,
    textTransform: 'uppercase',
  },
  // --- Dark footer ---
  darkFooter: {
    ...PaprikaSmartInventory.darkFooter,
    paddingHorizontal: 32,
    paddingTop: 24,
    paddingBottom: Platform.OS === 'web' ? 32 : 48,
    alignItems: 'center',
    gap: 24,
  },
  settingsBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
  },
  settingButton: {
    paddingVertical: 4,
    paddingHorizontal: 8,
  },
  settingLabel: {
    color: 'rgba(255,255,255,0.5)',
    fontSize: 10,
    fontFamily: 'Inter-SemiBold',
    fontWeight: '700',
    letterSpacing: 2,
    textTransform: 'uppercase',
  },
  settingLabelActive: {
    color: '#ffffff',
  },
  settingDivider: {
    width: 48,
    height: 1,
    backgroundColor: 'rgba(255,255,255,0.2)',
    borderRadius: 999,
  },
  shutterContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 24,
  },
  galleryButton: {
    width: 52,
    height: 52,
    justifyContent: 'center',
    alignItems: 'center',
  },
  shutterOuter: {
    width: 76,
    height: 76,
    borderRadius: 38,
    borderWidth: 4,
    borderColor: '#ffffff',
    justifyContent: 'center',
    alignItems: 'center',
  },
  shutterInner: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: '#ffffff',
  },
  hintText: {
    color: 'rgba(255,255,255,0.4)',
    fontSize: 11,
    fontFamily: 'Inter-Medium',
    fontWeight: '500',
    letterSpacing: 0.5,
  },
  // --- Preview state ---
  previewContainer: {
    flex: 1,
    position: 'relative',
  },
  capturedImage: {
    flex: 1,
    resizeMode: 'contain',
    backgroundColor: '#0a0a0a',
  },
  // --- Editorial loading UI ---
  analyzingContent: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 32,
    zIndex: 20,
  },
  heroSection: {
    alignItems: 'center',
    marginBottom: 48,
  },
  heroTitle: {
    ...PaprikaTypography.displayLarge,
    textAlign: 'center',
    color: Paprika.textPrimary,
    marginBottom: 16,
  },
  heroLine: {
    width: 64,
    height: 1,
    backgroundColor: Paprika.accent,
    opacity: 0.3,
  },
  valuePropSection: {
    marginBottom: 64,
    paddingHorizontal: 16,
    minHeight: 60,
    justifyContent: 'center',
  },
  valuePropText: {
    fontFamily: 'Newsreader-Italic',
    fontSize: 18,
    lineHeight: 28,
    color: Paprika.textSecondary,
    textAlign: 'center',
  },
  progressSection: {
    width: '100%',
    maxWidth: 280,
    alignItems: 'center',
    gap: 16,
  },
  progressTrack: {
    width: '100%',
    height: 2,
    backgroundColor: Paprika.surfaceHighest,
    borderRadius: 999,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: Paprika.accent,
    borderRadius: 999,
  },
  progressLabel: {
    fontSize: 11,
    fontFamily: 'Inter-SemiBold',
    fontWeight: '700',
    letterSpacing: 3,
    textTransform: 'uppercase',
    color: Paprika.textSecondary,
    opacity: 0.7,
  },
  previewActions: {
    flexDirection: 'row',
    gap: 16,
    width: '100%',
  },
  ghostButton: {
    flex: 1,
    height: 56,
    borderRadius: PaprikaRadius.full,
    backgroundColor: 'rgba(255,255,255,0.1)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  ghostButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontFamily: 'Inter-SemiBold',
    fontWeight: '700',
    letterSpacing: 1.5,
    textTransform: 'uppercase',
    textAlign: 'center',
  },
  primaryButton: {
    flex: 2,
    height: 56,
    borderRadius: PaprikaRadius.full,
    backgroundColor: Paprika.primaryContainer,
    justifyContent: 'center',
    alignItems: 'center',
  },
  primaryButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontFamily: 'Inter-SemiBold',
    fontWeight: '700',
    letterSpacing: 1.5,
    textTransform: 'uppercase',
    textAlign: 'center',
  },
  // --- Dev mode ---
  devModeIndicator: {
    position: 'absolute',
    zIndex: 20,
    backgroundColor: 'rgba(255, 193, 7, 0.9)',
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 8,
    alignItems: 'center',
  },
  devModeText: {
    color: '#000',
    fontSize: 14,
    fontWeight: 'bold',
  },
  devModeSubText: {
    color: '#000',
    fontSize: 11,
    marginTop: 2,
  },
});

// A guest never reaches the camera (#775): the scan behind it needs an account.
function InventoryCameraRoute() {
  const { analysisMode } = useLocalSearchParams<{ analysisMode?: string }>();
  return (
    <RequireAccountToScan
      kind={analysisMode === 'receipt' ? 'receipt' : 'fridge'}
      source="camera_screen"
      returnTo="/inventory"
    >
      <InventoryCameraScreen />
    </RequireAccountToScan>
  );
}

export default InventoryCameraRoute;
